import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import { companies } from '../../api/endpoints';
import { useAuth } from '../../stores/auth';

/**
 * The company the current route is about.
 *
 * The slug comes from the URL and the id from the token's company list — which
 * is the same list the tenant filter scopes every read by, so a slug the caller
 * cannot see never becomes a request at all.
 */
export function useCompanyId(): string {
  const { companySlug } = useParams({ from: '/authenticated/c/$companySlug' });
  const id = useAuth((state) => state.user?.companies.find((c) => c.slug === companySlug)?.id);

  return id ?? '';
}

/**
 * The slug as the URL spells it, for the screens that build links back into the
 * company scope. Read from the route rather than from the company payload so a
 * link can be built before the company query has resolved.
 */
export function useCompanySlug(): string {
  return useParams({ from: '/authenticated/c/$companySlug' }).companySlug;
}

export function useCompany() {
  const id = useCompanyId();

  return useQuery({ queryKey: ['company', id], queryFn: () => companies.get(id), enabled: id !== '' });
}
